import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AuthUserContext } from '../../components/auth-component';
import { PersonMultiSelect } from '../../components/v2/person-multi-select';
import { Person } from '../../types/person';

import { vaultClient } from './vault-client';

export function VaultNew() {
  const navigate = useNavigate();
  const { user } = useContext(AuthUserContext);
  const [ title, setTitle ] = useState('');
  const [ content, setContent ] = useState('');
  const [ password, setPassword ] = useState('');
  const [ confirmPassword, setConfirmPassword ] = useState('');
  const [ selectedRecipients, setSelectedRecipients ] = useState<Person[]>([]);
  const [ showPersonSelect, setShowPersonSelect ] = useState(false);
  const [ loading, setLoading ] = useState(false);
  const [ error, setError ] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    if (!content.trim()) {
      setError('Message content is required');
      return;
    }

    if (password && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (selectedRecipients.length === 0) {
      setError('Please select at least one recipient');
      return;
    }

    try {
      setLoading(true);
      await vaultClient.create({
        title: title.trim(),
        content: content.trim(),
        password: password || undefined,
        recipientIds: selectedRecipients.map(p => p.id)
      });
      navigate('/vault');
    } catch (err) {
      setError('Failed to create message');
    } finally {
      setLoading(false);
    }
  };

  const handleRecipientsSelect = (recipients: Person[]) => {
    setSelectedRecipients(recipients);
    setShowPersonSelect(false);
  };

  const handleRemoveRecipient = (recipientId: string) => {
    setSelectedRecipients(prev => prev.filter(p => p.id !== recipientId));
  };

  const handleCancel = () => {
    navigate('/vault');
  };

  return (
    <div className="vault-page">
      <div className="screen-container">
        <div className="screen-header">
          <div className="screen-header-back-button">
            <button className="back-button" onClick={handleCancel}>
              <span className="material-symbols-outlined">arrow_back</span>
            </button>
          </div>
          <div className="screen-title">
            <h2>Create Secret Message</h2>
            <p>Write a private message for your loved ones</p>
          </div>
        </div>

        <div className="screen-content">
          <form onSubmit={handleSubmit} className="vault-form">
            {error && (
              <div className="error-container">
                <p className="error-message">{error}</p>
              </div>
            )}

            <div className="input">
              <label htmlFor="title">Message Title</label>
              <input
                id="title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter a title for your message"
                required
              />
            </div>

            <div className="input">
              <label htmlFor="content">Message Content</label>
              <textarea
                id="content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Write your message here..."
                rows={8}
                required
              />
            </div>

            <div className="input">
              <label htmlFor="password">Password (Optional)</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter a password to lock this message"
              />
              <p className="input-help">
                If you set a password, only you will be able to read this message
              </p>
            </div>

            {password && (
              <div className="input">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                />
              </div>
            )}

            <div className="input">
              <label>Recipients</label>
              <div className="recipients-container">
                {selectedRecipients.length > 0 && (
                  <div className="selected-recipients">
                    {selectedRecipients.map((recipient) => (
                      <div key={recipient.id} className="selected-recipient">
                        <span className="recipient-name">
                          {recipient.firstName} {recipient.lastName}
                        </span>
                        <button
                          type="button"
                          className="remove-recipient"
                          onClick={() => handleRemoveRecipient(recipient.id)}
                        >
                          <span className="material-symbols-outlined">close</span>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                <button
                  type="button"
                  className="button secondary add-recipients-button"
                  onClick={() => setShowPersonSelect(true)}
                >
                  <span className="material-symbols-outlined">person_add</span>
                  {selectedRecipients.length === 0 ? 'Add Recipients' : 'Add More Recipients'}
                </button>
              </div>
            </div>

            <div className="button-container">
              <button
                type="button"
                className="button secondary"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="button"
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Message'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {showPersonSelect && (
        <div className="modal-overlay">
          <div className="modal-content">
            <PersonMultiSelect
              selected={selectedRecipients}
              handleSelectOptions={handleRecipientsSelect}
              handleClickableOption={() => {
                // Handle adding new person - could navigate to person creation
                console.log('Add new person clicked');
              }}
              multiple={true}
              handleCancel={() => setShowPersonSelect(false)}
              inModal={true}
            />
          </div>
        </div>
      )}
    </div>
  );
}
